/**
 * Simple observable store tailored for the filters rebuild.
 *
 * @typedef {import('../types').FiltersConfig} FiltersConfig
 * @typedef {import('../types').FiltersState} FiltersState
 * @typedef {import('../types').FiltersStore} FiltersStore
 */

const EMPTY_DIALOG_MAP = Object.freeze({});

/**
 * Clone the current state into a plain object for safe consumption.
 * @param {InternalFiltersState} state
 * @returns {FiltersState}
 */
function cloneState(state) {
  return {
    activeMarketKey: state.activeMarketKey,
    activeMatchupId: state.activeMatchupId,
    positionFilters: new Set(state.positionFilters),
    sortOption: state.sortOption,
    searchTerm: state.searchTerm,
    dialogOpenMap: { ...state.dialogOpenMap },
  };
}

/**
 * @typedef {FiltersState & {
 *   dialogOpenMap: Record<string, boolean>,
 * }} InternalFiltersState
 */

/**
 * @param {FiltersConfig} config
 * @returns {FiltersStore}
 */
export function createFiltersStore(config) {
  const listeners = new Set();
  const {
    currentMarket,
    filters: filtersConfig = /** @type {FiltersConfig['filters']} */ ({
      positions: [],
      sortOptions: [],
      urlParams: { matchups: [], positions: [], search: '' },
    }),
  } = config || {};

  const initialMatchups =
    (filtersConfig.urlParams && Array.isArray(filtersConfig.urlParams.matchups)
      ? filtersConfig.urlParams.matchups
      : []
    ).filter(Boolean);
  const initialMatchupParam =
    typeof filtersConfig.urlParams?.matchup === 'string'
      ? filtersConfig.urlParams.matchup.trim()
      : '';
  const initialMatchupId =
    initialMatchupParam ||
    (initialMatchups.length ? String(initialMatchups[0]).trim() : '');

  const initialPositions =
    (filtersConfig.urlParams &&
    Array.isArray(filtersConfig.urlParams.positions)
      ? filtersConfig.urlParams.positions
      : []
    ).filter(Boolean);

  const initialSearch =
    filtersConfig.urlParams && typeof filtersConfig.urlParams.search === 'string'
      ? filtersConfig.urlParams.search
      : '';

  const initialSort =
    typeof filtersConfig.urlParams?.sort === 'string'
      ? filtersConfig.urlParams.sort
      : filtersConfig.sortOptions?.[0]?.value || '';

  /** @type {InternalFiltersState} */
  const state = {
    activeMarketKey: currentMarket?.key || '',
    activeMatchupId: initialMatchupId,
    positionFilters: new Set(initialPositions),
    sortOption: initialSort,
    searchTerm: initialSearch,
    dialogOpenMap: { ...EMPTY_DIALOG_MAP },
  };

  /**
   * Notify all subscribers with the updated state.
   */
  function notify() {
    const snapshot = cloneState(state);
    listeners.forEach((listener) => {
      try {
        listener(snapshot);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('[filters-store] listener error', error);
        }
      }
    });
  }

  /**
   * @param {Partial<FiltersState>} partial
   */
  function mergeState(partial) {
    if (!partial || typeof partial !== 'object') {
      return;
    }

    if (typeof partial.activeMarketKey === 'string') {
      state.activeMarketKey = partial.activeMarketKey;
    }
    if (typeof partial.activeMatchupId === 'string') {
      state.activeMatchupId = partial.activeMatchupId;
    }
    if (partial.positionFilters instanceof Set) {
      state.positionFilters = new Set(partial.positionFilters);
    } else if (Array.isArray(partial.positionFilters)) {
      state.positionFilters = new Set(partial.positionFilters);
    }
    if (typeof partial.sortOption === 'string') {
      state.sortOption = partial.sortOption;
    }
    if (typeof partial.searchTerm === 'string') {
      state.searchTerm = partial.searchTerm;
    }
    if (partial.dialogOpenMap && typeof partial.dialogOpenMap === 'object') {
      state.dialogOpenMap = {
        ...state.dialogOpenMap,
        ...partial.dialogOpenMap,
      };
    }
  }

  /**
   * @param {Partial<FiltersState>} partial
   */
  function setState(partial) {
    mergeState(partial);
    notify();
  }

  function getState() {
    return cloneState(state);
  }

  /**
   * @param {(state: FiltersState) => void} listener
   * @returns {() => void}
   */
  function subscribe(listener) {
    if (typeof listener !== 'function') {
      return () => {};
    }
    listeners.add(listener);
    listener(cloneState(state));
    return () => {
      listeners.delete(listener);
    };
  }

  function setActiveMarket(key) {
    if (!key || key === state.activeMarketKey) {
      return;
    }
    state.activeMarketKey = key;
    notify();
  }

  function setActiveMatchup(id) {
    const next = typeof id === 'string' ? id.trim() : '';
    if (next === state.activeMatchupId) {
      return;
    }
    state.activeMatchupId = next;
    notify();
  }

  function setPositionFilters(values) {
    if (!Array.isArray(values)) {
      return;
    }
    const next = values.filter(Boolean);
    state.positionFilters = new Set(next);
    notify();
  }

  function togglePosition(value) {
    if (!value) {
      return;
    }
    const next = new Set(state.positionFilters);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    state.positionFilters = next;
    notify();
  }

  function clearPositions() {
    if (!state.positionFilters.size) {
      return;
    }
    state.positionFilters = new Set();
    notify();
  }

  function setSortOption(value) {
    if (!value || value === state.sortOption) {
      return;
    }
    state.sortOption = value;
    notify();
  }

  function setSearchTerm(value) {
    if (value === state.searchTerm) {
      return;
    }
    state.searchTerm = value || '';
    notify();
  }

  function setDialogOpen(key, isOpen) {
    if (!key) {
      return;
    }
    const bool = Boolean(isOpen);
    if (state.dialogOpenMap[key] === bool) {
      return;
    }
    state.dialogOpenMap = {
      ...state.dialogOpenMap,
      [key]: bool,
    };
    notify();
  }

  const actions = {
    setActiveMarket,
    setActiveMatchup,
    setPositionFilters,
    togglePosition,
    clearPositions,
    setSortOption,
    setSearchTerm,
    setDialogOpen,
  };

  return {
    config,
    getState,
    setState,
    subscribe,
    actions,
  };
}
